/**
 * UWM's dynamic form (the *-pricing-layout fields) for Mana Pricing: default seeding,
 * request-body coercion and the "shared fields" bridge. Carries the rules of the retired
 * Pricing module's page (removed 2026-10-11) so UWM receives exactly the request it
 * always has:
 *   - state/county are never sent (UWM derives them from the ZIP)
 *   - hidden-but-sent fields keep their layout defaults
 *   - the buydown alias is shown for the client-side math but never sent
 *   - TRAC+ title adds tracIncentiveID "1" (credit applied as an LLPA)
 *
 * Mana Pricing edits the fields both engines share (loan amount, purpose, terms, …) once,
 * in the lender-sheet catalog. `patchUwmBody` carries only the edits the MLO actually made
 * into the stored UWM request (a three-way merge), so re-pricing an unchanged saved
 * scenario sends UWM byte-for-byte what it sent before.
 */
import { lenderInputsToUwm, lenderInputsToUwmHeloc, type UwmLayoutField } from './bridge.js';

export type UwmFieldValue = string | string[];

export const UWM_MULTI = 'Dropdown-MultiSelect';
/** Display-only Michigan defaults in the layout; UWM derives geography from the ZIP. */
export const UWM_GEO_DERIVED = new Set(['PropertyState', 'PropertyCounty']);
/** Shown for the client-side buydown math, never sent on the quote itself (UWM would filter it to that buydown's products); priceUwm asks UWM separately which products allow each buydown. */
export const UWM_CLIENT_ONLY = new Set(['BuyDownAliasID']);
/** Fixed/default values — hidden in the form but seeded and sent. */
export const UWM_HIDDEN_FIELDS = new Set(['MonthlyDebt', 'AnnualHomeownersInsurance', 'AnnualPercentageRateFees', 'EmploymentOptions', 'AnnualTaxes', 'LoanShieldTypeId']);
export const UWM_FORCE_SHOW = new Set(['BuyDownAliasID']);
/** An empty term list makes UWM return zero products. */
export const UWM_REQUIRED_OVERRIDE = new Set(['LoanTermIds']);
/** Refinance 1st mortgage (3) and construction refinance (12) show Refinance Purpose. */
export const UWM_REFI_PURPOSE_IDS = new Set(['3', '12']);
export const UWM_DEFAULT_PICK: Record<string, string> = { TRACTypeId: 'TRAC+' };
export const UWM_COMPANY_NAME_FIELD = 'ObfuscatedBusinessEntityId';
/** Body keys that are numbers in UWM's quote model (the layout input type is not reliable). */
export const UWM_NUMBER_KEYS = new Set([
  'loanAmount', 'secondLoanAmount', 'salesPrice', 'appraisedValue', 'loanToValue',
  'creditScore', 'monthlyIncome', 'monthlyDebt', 'numberOfUnits',
  'numberOfFinancedProperties', 'numberOfBorrowers', 'debtServiceCreditRatio',
  'payoffAmount', 'controlYourPrice', 'annualTaxes', 'annualHomeownersInsurance',
  'annualPercentageRateFees', 'targetPriceValue', 'targetCashValue', 'targetRateValue',
  'firstLienAmount', 'totalLineAmount', 'initialDrawAmount',
]);

/**
 * UWM body keys the Mana Pricing scenario panel controls (the shared fields); every other
 * layout field is edited in the UWM-only section. Matches what lenderInputsToUwm produces.
 */
export const UWM_MANAGED_BODY_KEYS = [
  'loanTypeIds', 'loanAmount', 'appraisedValue', 'salesPrice', 'loanToValue', 'purposeTypeId', 'refinancePurposeID',
  'occupancyTypeId', 'propertyTypeID', 'numberOfUnits', 'attachmentTypeID', 'propertyZipCode', 'creditScore', 'monthlyIncome',
  'isDTIOver45Percent', 'firstTimeHomeBuyer', 'loanTermIds', 'commitmentPeriodID', 'escrowWaiverTypeId', 'compensationPayerTypeID',
  'useMortgageInsuranceTypeIds', 'financedMortgageInsuranceTypeId', 'prePaymentPenaltyId',
] as const;
const MANAGED = new Set<string>(UWM_MANAGED_BODY_KEYS);
/** UWM HELOC body keys the shared home-equity fields control (heloc-pricing-layout). */
export const UWM_HELOC_MANAGED_BODY_KEYS = [
  'creditScore', 'propertyZipCode', 'firstLienAmount', 'appraisedValue', 'totalLineAmount', 'initialDrawAmount',
  'occupancyTypeId', 'loanTermIds', 'refinancePurposeID', 'commitmentPeriodID', 'propertyTypeID', 'numberOfUnits',
  'attachmentTypeID', 'homeEquityTypeId',
] as const;
const MANAGED_HELOC = new Set<string>(UWM_HELOC_MANAGED_BODY_KEYS);

export const uwmBodyKey = (itemId: string): string => itemId.charAt(0).toLowerCase() + itemId.slice(1);
export const isUwmNumberField = (t: string): boolean => t.startsWith('Number-') || t === 'Calendar';
export const isUwmBoolField = (f: UwmLayoutField): boolean =>
  f.displayInputType === 'Dropdown' && (f.selectListData?.length ?? 0) > 0 && (f.selectListData ?? []).every((o) => o.id === 'true' || o.id === 'false');
export const isUwmManagedField = (f: UwmLayoutField, productType: 'mortgage' | 'heloc' = 'mortgage'): boolean =>
  (productType === 'heloc' ? MANAGED_HELOC : MANAGED).has(uwmBodyKey(f.itemId)) || UWM_CLIENT_ONLY.has(f.itemId);

/** Default values for every layout field (same picks as the Pricing module). */
export function seedUwmValues(layout: UwmLayoutField[]): Record<string, UwmFieldValue> {
  const seed: Record<string, UwmFieldValue> = {};
  for (const f of layout) {
    if (f.itemId === 'BorrowerName') continue;
    if (f.displayInputType === UWM_MULTI) {
      seed[f.itemId] = [];
      continue;
    }
    const opts = f.selectListData ?? [];
    const pickSub = UWM_DEFAULT_PICK[f.itemId];
    const picked = pickSub ? opts.find((o) => o.displayValue.toLowerCase().includes(pickSub.toLowerCase())) : undefined;
    const own = opts.find((o) => /mana home loans/i.test(o.displayValue));
    const only = opts.length === 1 ? opts[0] : undefined;
    seed[f.itemId] = picked?.id ?? own?.id ?? only?.id ?? f.defaultValue ?? '';
  }
  return seed;
}

/** Form values from a saved UWM request body (every layout field the body carries). */
export function uwmValuesFromBody(layout: UwmLayoutField[], body: Record<string, unknown>, base?: Record<string, UwmFieldValue>): Record<string, UwmFieldValue> {
  const next: Record<string, UwmFieldValue> = { ...(base ?? seedUwmValues(layout)) };
  for (const f of layout) {
    if (f.itemId === 'BorrowerName') continue;
    const sv = body[uwmBodyKey(f.itemId)];
    if (sv === undefined) continue;
    next[f.itemId] = f.displayInputType === UWM_MULTI ? (Array.isArray(sv) ? sv.map(String) : []) : sv == null ? '' : String(sv);
  }
  return next;
}

/**
 * The quote request body from form values — identical coercion to the Pricing module:
 * every layout field except BorrowerName, state/county and the buydown alias; multi-selects
 * as string arrays, true/false dropdowns as booleans, number keys as numbers, the rest strings.
 * `extras` (keys of a saved body that are not layout fields, e.g. brokerAlias) ride along.
 */
export function buildUwmBody(layout: UwmLayoutField[], values: Record<string, UwmFieldValue>, extras?: Record<string, unknown>): Record<string, unknown> {
  const layoutKeys = new Set(layout.map((f) => uwmBodyKey(f.itemId)));
  const body: Record<string, unknown> = {};
  if (extras) for (const [k, v] of Object.entries(extras)) if (!layoutKeys.has(k) && k !== 'tracIncentiveID' && k !== 'borrowerName') body[k] = v;
  for (const f of layout) {
    if (f.itemId === 'BorrowerName' || UWM_GEO_DERIVED.has(f.itemId) || UWM_CLIENT_ONLY.has(f.itemId)) continue;
    const v = values[f.itemId];
    const key = uwmBodyKey(f.itemId);
    if (f.displayInputType === UWM_MULTI) body[key] = Array.isArray(v) ? v : [];
    else if (isUwmBoolField(f)) body[key] = v === 'true';
    else if (UWM_NUMBER_KEYS.has(key) || (isUwmNumberField(f.displayInputType) && !/id$/i.test(key))) {
      const n = Number(v);
      body[key] = Number.isFinite(n) ? n : 0;
    } else body[key] = typeof v === 'string' ? v : '';
  }
  if (body.tRACTypeId === '2') body.tracIncentiveID = '1';
  return body;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Coerce a bridge value onto a layout field's body type (e.g. "1" id for a dropdown, number for amounts). */
function coerceForField(f: UwmLayoutField | undefined, key: string, v: unknown): unknown {
  if (Array.isArray(v)) return v.map(String);
  if (f && isUwmBoolField(f)) return v === true || v === 'true';
  if (UWM_NUMBER_KEYS.has(key)) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof v === 'boolean') return v;
  return v == null ? '' : typeof v === 'number' ? String(v) : v;
}

/**
 * Carry the MLO's edits of the shared fields into a UWM request body. For each managed key,
 * compare what the bridge derives from the canonical scenario NOW with what it derived from
 * the BASELINE (the canonical scenario when the body was last synchronized); only keys whose
 * derivation changed are overwritten. With no baseline (a new scenario) every managed key is set.
 */
export function patchUwmBody(args: {
  layout: UwmLayoutField[] | null;
  body: Record<string, unknown>;
  canonicalNow: Record<string, unknown>;
  canonicalBaseline: Record<string, unknown> | null;
  /** HELOC: the home-equity fields drive UWM's HELOC request instead of the mortgage one. */
  productType?: 'mortgage' | 'heloc';
}): { body: Record<string, unknown>; changedKeys: string[]; notes: string[] } {
  const { layout } = args;
  const heloc = args.productType === 'heloc';
  const translate = heloc ? lenderInputsToUwmHeloc : lenderInputsToUwm;
  const now = translate(args.canonicalNow, layout);
  const prev = args.canonicalBaseline ? translate(args.canonicalBaseline, layout).inputs : null;
  const byKey = new Map((layout ?? []).map((f) => [uwmBodyKey(f.itemId), f]));
  const body = { ...args.body };
  const changedKeys: string[] = [];
  for (const key of heloc ? UWM_HELOC_MANAGED_BODY_KEYS : UWM_MANAGED_BODY_KEYS) {
    const nextVal = now.inputs[key];
    if (nextVal === undefined) continue;
    if (prev && same(prev[key], nextVal)) continue;
    const f = byKey.get(key);
    if (layout && !f && !(key in body)) continue; // not a field of this layout
    const coerced = coerceForField(f, key, nextVal);
    if (!same(body[key], coerced)) {
      body[key] = coerced;
      changedKeys.push(key);
    }
  }
  if (body.tRACTypeId === '2') body.tracIncentiveID = '1';
  return { body, changedKeys, notes: now.notes };
}

/** The UWM-only section: displayed layout fields the shared panel does not control. */
export function uwmSectionFields(
  layout: UwmLayoutField[],
  values: Record<string, UwmFieldValue>,
  opts: { hideCompanyName: boolean; productType: 'mortgage' | 'heloc' },
): UwmLayoutField[] {
  const purpose = values['PurposeTypeId'];
  const showRefiPurpose = typeof purpose === 'string' && UWM_REFI_PURPOSE_IDS.has(purpose);
  return layout.filter(
    (f) =>
      (f.isDisplay || UWM_FORCE_SHOW.has(f.itemId) || (f.itemId === 'RefinancePurposeID' && showRefiPurpose)) &&
      f.itemId !== 'BorrowerName' &&
      !UWM_GEO_DERIVED.has(f.itemId) &&
      !UWM_HIDDEN_FIELDS.has(f.itemId) &&
      !(opts.hideCompanyName && f.itemId === UWM_COMPANY_NAME_FIELD) &&
      // The shared panel edits the managed fields of either form (mortgage or HELOC).
      !isUwmManagedField(f, opts.productType),
  );
}

/** Required UWM fields (of the given list) that are still empty. */
export function missingUwmFields(fields: UwmLayoutField[], values: Record<string, UwmFieldValue>): UwmLayoutField[] {
  return fields.filter((f) => {
    if (f.itemId === 'BorrowerName' || UWM_GEO_DERIVED.has(f.itemId) || UWM_CLIENT_ONLY.has(f.itemId)) return false;
    const req = (f.isRequired && f.isDisplay) || UWM_REQUIRED_OVERRIDE.has(f.itemId);
    if (!req) return false;
    const v = values[f.itemId];
    return v == null || v === '' || (Array.isArray(v) && v.length === 0);
  });
}
